import config from '@root/src/config';
import { isEmpty, isArrayOfObjects } from '@root/src/utils/commons/objects';
import FetchError from '@services/api/utils/FetchError';
import { isObject } from 'formik';
import { GetServerSidePropsContext } from 'next';
import { parseCookies } from 'nookies';
import { ParsedUrlQuery } from 'querystring';

export type CtxType = GetServerSidePropsContext<ParsedUrlQuery>;

export enum RequestMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

/**
 * Build and Fetch request
 * @param {string} endpoint - url
 * @param {RequestMethod} method - request method
 * @param {object} params - url entity id
 * @param {object} query - url params
 * @param {BackBody} body - request body
 * @param {AbortSignal} signal
 * @param {CtxType} ctx - context
 * @param {Function} responseFormatter - Function to format data from back to front
 */
export const fetchRequest = <FrontEntity, BackEntity, BackBody = never>({
  endpoint,
  method,
  responseFormatter,
  params,
  query,
  body,
  signal,
  ctx,
  protectedRoute,
}: {
  endpoint: string;
  method: RequestMethod;
  params?: object;
  query?: object;
  body?: BackBody;
  signal?: AbortSignal;
  ctx?: CtxType;
  protectedRoute: boolean;
  responseFormatter?: (data: BackEntity) => FrontEntity;
}): Promise<FrontEntity> => {
  let url = endpoint;

  // Build params
  if (params && !isEmpty(params)) {
    url = Object.entries(params).reduce(
      (s, [v, k]: [string, string]) => s.split(`:${v}`).join(k), // Avoid using RegExp entirely
      endpoint,
    );
  }

  if (url[url.length - 1] !== '/') {
    url += '/';
  }

  // Build query
  if (query && !isEmpty(query)) {
    const res = Object.entries(query)
      .filter(([k, v]) => {
        if (typeof v === 'string') {
          return v.length >= 2;
        }
        if (Array.isArray(v)) {
          return v.length > 0;
        }
        return true;
      })
      .map(([k, v]) => {
        if (v == null) {
          return '';
        }
        if (Array.isArray(v)) {
          if (v.length === 0) {
            return `${k}=[]`;
          }
          if (isArrayOfObjects(v)) {
            return `${k}=${JSON.stringify(v)}`;
          }
          return `${k}=${v.join(',')}`;
        }
        if (isObject(v)) {
          return `${k}=${JSON.stringify(v)}`;
        }
        return `${k}=${v as string}`;
      })
      .join('&');
    url += `?${res}`;
  }

  const cookies = parseCookies(ctx);
  const token = protectedRoute ? cookies.api_backend_token : undefined;

  return callApi<FrontEntity, BackEntity, BackBody>({
    endpoint: url,
    method,
    token,
    body,
    signal,
    responseFormatter,
  });
};

/**
 * Construct, send request and format result
 * @param {string} endpoint - url
 * @param {RequestMethod} method - request method
 * @param {string} token - token for secured url
 * @param {BackBody} body - request body
 * @param {AbortSignal} signal
 * @param {string} contentType - request contentType
 * @param {Function} responseFormatter - Function to format data from back to front
 */
export const callApi = async <FrontEntity, BackEntity, BackBody>({
  endpoint,
  method,
  token,
  body,
  signal,
  contentType = 'application/json',
  responseFormatter,
}: {
  endpoint: string;
  method: RequestMethod;
  token?: string;
  body?: BackBody;
  signal?: AbortSignal;
  contentType?: string;
  responseFormatter?: (data: BackEntity) => FrontEntity;
}): Promise<FrontEntity> => {
  if (!endpoint) {
    throw Error('endpoint is required');
  }
  if (!method) {
    throw Error('method is required');
  }

  const url = `${config.url.urlApi}${endpoint}`;

  const headers = new Headers({
    'Content-Type': contentType,
    Connection: 'keep-alive',
  });
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const options: Parameters<typeof fetch>[1] = {
    method,
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  };

  if (body) {
    options.body = JSON.stringify(body);
  }
  if (signal) {
    options.signal = signal;
  }

  if (config.devTools) {
    /* eslint-disable no-console */
    console.log('---------------- Fetch ------------------------------------------------------');
    console.log(url, options);
    console.log('-----------------------------------------------------------------------------');
  }

  const response = await fetch(url, options);
  if (!response.ok) {
    const res = (await response.json()) as { message: string };
    throw new FetchError(response, `${res.message}-${response.status}`);
  }
  return responseFormatter
    ? responseFormatter((await response.json()) as BackEntity)
    : ((await response.json()) as FrontEntity);
};
